import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { GridApi, GridReadyEvent, RowDragEvent } from 'ag-grid-community'
import {
  ProgressReportByTeamPageConfig,
  VersionReportStatus,
  VersionReportVersionPageRowEntity,
} from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ReorderRow } from '../../../../../applications/ports/versionReportVersionRepository'
import {
  ProgressReportTable,
  ProgressReportTableContent,
} from '../table/ProgressReportTable'
import { DeliverableTaskPopper } from '../dialog/DeliverableTaskPopper'
import {
  ProgressValue,
  WbsItemAggregateCategory,
} from '../../../../../domain/value-object/ProjectPlanSnapshot'
import { useProjectPlanSnapshotRepository } from '../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { aggregatedValueService } from '../../../../../domain/value-object/AggregatedValueVO'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../domain/entity/WbsItemEntity'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { useTeamReportColumnDef } from '../../hooks/columnDef'
import { useProgressReportDragAndDrop } from '../../hooks/dragAndDrop'
import { useDiscussionActionContext } from '../../context/DiscussionContext'
import { TargetCandidate } from '../../../../../domain/entity/DiscussionEntity'
import { PendingEdits } from '../../hooks/currentReport'

type ProgressReportByTeamProps = {
  snapshotUuid: string
  pageUuid: string
  editable: boolean
  config: ProgressReportByTeamPageConfig
  rows: VersionReportVersionPageRowEntity[]
  updateRow: (
    rowUuid: string,
    lockVersion: number,
    key: 'status' | 'comment',
    value: string
  ) => Promise<boolean>
  applyReorderedRows: (
    pageUuid: string,
    reorderPayload: ReorderRow[]
  ) => Promise<void>
  pendingEditsRef: MutableRefObject<PendingEdits>
  discardToken: number
  changeLength: number
  onEvaluateRow?: (targetUuid: string) => Promise<void>
}
export const ProgressReportByTeam = ({
  editable,
  snapshotUuid,
  pageUuid,
  config,
  rows,
  updateRow,
  applyReorderedRows,
  pendingEditsRef,
  discardToken,
  changeLength,
  onEvaluateRow,
}: ProgressReportByTeamProps) => {
  const [progressValues, setProgressValues] = useState<ProgressValue[]>([])
  const { pivotByTeamUuids } = useProjectPlanSnapshotRepository()
  const { setTargetCandidates } = useDiscussionActionContext()

  useEffect(() => {
    const candidates: TargetCandidate[] = progressValues.map(v => ({
      uuid: v.uuid,
      name: v.name,
      type: 'TEAM',
    }))
    setTargetCandidates(candidates)
    return () => {
      setTargetCandidates([])
    }
  }, [progressValues, setTargetCandidates])

  const gridApiRef = useRef<GridApi | null>(null)
  const [isGridReady, setIsGridReady] = useState(false)
  const {
    contextRef,
    onRowDragEnter,
    onRowDragMove,
    onRowDragEnd,
    onRowDragLeave,
    skipNextFetchRef,
  } = useProgressReportDragAndDrop({
    gridApi: isGridReady ? gridApiRef.current || undefined : undefined,
    progressValues,
    setProgressValues,
    pageUuid,
    rows,
    applyReorderedRows,
  })
  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApiRef.current = e.api
    setIsGridReady(true)
  }, [])
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false
      return
    }
    if (!config.rootUuid || config.tableRows.length === 0) {
      setProgressValues([])
      return
    }
    const fn = async () => {
      const response = await pivotByTeamUuids(
        snapshotUuid,
        config.rootUuid,
        config.tableRows
      )
      setProgressValues(response)
    }
    fn()
  }, [
    config.rootUuid,
    config.tableRows,
    pivotByTeamUuids,
    snapshotUuid,
    skipNextFetchRef,
  ])
  const tableContents = useMemo(
    () =>
      progressValues.map(v => {
        const row = rows.find(r => r.targetUuid === v.uuid)
        return {
          uuid: v.uuid,
          name: v.name,
          status:
            (row
              ? (pendingEditsRef.current.rows.get(row.uuid)?.status ??
                row.status)
              : undefined) || 'SUNNY',
          comment:
            (row
              ? (pendingEditsRef.current.rows.get(row.uuid)?.comment ??
                row.comment)
              : undefined) || '',
          scheduledProgressRate:
            aggregatedValueService.getFormattedRatePercentage(
              v.planned,
              v.total,
              WbsItemType.TASK,
              AggregateField.WBS_ITEM_COUNT
            ),
          progressRate: aggregatedValueService.getFormattedRatePercentage(
            v.completed,
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          total: aggregatedValueService.getFormatted(
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          planned: aggregatedValueService.getFormatted(
            v.planned,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          completed: aggregatedValueService.getFormatted(
            v.completed,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          delay: aggregatedValueService.getFormatted(
            v.delay,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          remaining: aggregatedValueService.getFormatted(
            v.remaining,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          wbsItemStatus: v.wbsItemStatus,
          team: v.team,
          progressRateDiff: aggregatedValueService.getFormattedRatePercentage(
            v.plannedCompletedDiff,
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          scheduledStartDate: dateV2Service
            .fromString(v.scheduledDate?.startDate)
            ?.toDateString(),
          scheduledEndDate: dateV2Service
            .fromString(v.scheduledDate?.endDate)
            ?.toDateString(),
          maxChildScheduledEndDate: v.maxChildScheduledEndDate,
          preceding: aggregatedValueService.getFormatted(
            v.preceding,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          unplanned: aggregatedValueService.getFormatted(
            v.unplanned,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pendingEditsRef は ref なので依存不要
    [rows, progressValues, discardToken, changeLength]
  )
  const ref = useRef<HTMLDivElement>(null)
  const updateTableContent = useCallback(
    (
      v: { uuid: string; status: VersionReportStatus; comment: string },
      _index: number
    ) => {
      const row = rows.find(r => r.targetUuid === v.uuid)
      if (!row) return
      const pendingStatus =
        pendingEditsRef.current.rows.get(row.uuid)?.status ?? row.status
      const pendingComment =
        pendingEditsRef.current.rows.get(row.uuid)?.comment ?? row.comment
      if (v.status !== pendingStatus) {
        void updateRow(row.uuid, row.lockVersion, 'status', v.status)
      } else if (v.comment !== pendingComment) {
        void updateRow(row.uuid, row.lockVersion, 'comment', v.comment)
      }
    },
    [rows, updateRow, pendingEditsRef]
  )

  const [popperState, setPopperState] = useState<{
    anchorEl: HTMLElement
    clickPosition: { x: number; y: number }
    teamUuid: string
    aggregateType: WbsItemAggregateCategory
  } | null>(null)
  const closePopper = useCallback(() => setPopperState(null), [])
  const handleNumericCellClick = useCallback(
    (
      data: ProgressReportTableContent,
      anchorEl: HTMLElement,
      clickPosition: { x: number; y: number },
      aggregateType: WbsItemAggregateCategory
    ) => {
      if (!config.rootUuid) return
      setPopperState({
        anchorEl,
        clickPosition,
        teamUuid: data.uuid,
        aggregateType,
      })
    },
    [config.rootUuid]
  )
  const handleRowDragEnter = useCallback(
    (event: RowDragEvent) => {
      setPopperState(null)
      onRowDragEnter(event)
    },
    [onRowDragEnter]
  )

  useEffect(() => {
    if (!editable) setPopperState(null)
    gridApiRef.current?.refreshCells({ force: true })
  }, [editable])

  const columns = useTeamReportColumnDef({
    editable,
    updateTableContent,
    onNumericCellClick: handleNumericCellClick,
  })
  return (
    <>
      <ProgressReportTable
        ref={ref}
        columns={columns}
        editable={editable}
        contents={tableContents}
        context={contextRef.current}
        onGridReady={onGridReady}
        onRowDragEnter={handleRowDragEnter}
        onRowDragMove={onRowDragMove}
        onRowDragLeave={onRowDragLeave}
        onRowDragEnd={onRowDragEnd}
        onEvaluateRow={onEvaluateRow}
      />
      <DeliverableTaskPopper
        anchorEl={popperState?.anchorEl ?? null}
        clickPosition={popperState?.clickPosition}
        rootUuid={config.rootUuid}
        teamUuid={popperState?.teamUuid}
        aggregateType={popperState?.aggregateType}
        aggregateTarget={WbsItemType.TASK}
        snapshotUuid={snapshotUuid}
        onClose={closePopper}
      />
    </>
  )
}
